import { Suspense, lazy } from "react"
import type { Me, PublicConfig } from "@shared/api"
import { AppShell, type ViewId } from "./components/AppShell"
import { AuthGate } from "./components/AuthGate"
import { ErrorState } from "./components/ui"
import { AuthProvider } from "./context/AuthContext"
import { useApi } from "./hooks/useApi"
import { useQueryState } from "./hooks/useQueryState"
import { api } from "./services/api"
import { RequestsPage } from "./pages/RequestsPage"

// Las páginas con gráficos van LAZY: son lo único que importa recharts, así que el
// chunk vendor-charts se baja recién al abrirlas y la ruta inicial se queda dentro
// del presupuesto de ≤120 kB gzip (DASHBOARD-PLAN §8).
const TimePage = lazy(() => import("./pages/TimePage"))
const HealthPage = lazy(() => import("./pages/HealthPage"))
// Recurrencias está APAGADA por ahora: la página sigue en pages/RecurrencesPage.tsx (y sus
// dos GET en src/api.ts), pero sin item de nav ni ruta, así que un ?view=recurrences viejo
// cae en el else de abajo. Para reactivarla: (1) volver a poner "recurrences" en ViewId
// (AppShell.tsx), (2) el item en su array de nav, (3) su lazy() acá y la rama de render.
// Iba lazy porque su CSS de heatmap pesa, aunque no use recharts.

export function App({ config }: { config: PublicConfig }) {
  return (
    <AuthProvider>
      <AuthGate>{(me) => <Dashboard me={{ ...me, displayTimezone: me.displayTimezone || config.displayTimezone }} />}</AuthGate>
    </AuthProvider>
  )
}

function Dashboard({ me }: { me: Me }) {
  const { query, setQuery, resetQuery } = useQueryState()
  // El default es el PRIMER item del sidebar (ver el array de AppShell.tsx): entrar sin
  // query y clickear el primero tienen que llevar al mismo lugar.
  const view = (query.view as ViewId) ?? "time"

  // Los catálogos salen de src/config.ts vía la API: así los chips y las etiquetas del
  // tablero no pueden divergir del CHECK de la base ni del DM de Slack.
  const catalogs = useApi((signal) => api.catalogs(signal), [])

  if (catalogs.error) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <ErrorState
            title="No se pudieron cargar los catálogos"
            message={catalogs.error.message}
            onRetry={catalogs.refetch}
          />
        </div>
      </div>
    )
  }

  if (!catalogs.data) {
    return (
      <div className="auth-page">
        <div className="auth-loading">Cargando catálogos…</div>
      </div>
    )
  }

  return (
    <AppShell me={me} view={view} onNavigate={(v) => setQuery({ view: v, id: undefined })}>
      <Suspense fallback={<div className="card">Cargando…</div>}>
        {view === "requests" ? (
          <RequestsPage
            me={me}
            catalogs={catalogs.data}
            query={query}
            setQuery={setQuery}
            resetQuery={resetQuery}
          />
        ) : view === "time" ? (
          <TimePage me={me} query={query} setQuery={setQuery} />
        ) : view === "health" ? (
          <HealthPage onOpenRequest={(id) => setQuery({ view: "requests", id })} />
        ) : (
          // Alcanzable escribiendo un ?view= que no existe a mano, o con un bookmark de
          // ?view=recurrences de cuando esa pantalla estaba prendida.
          <div className="card">
            <ErrorState
              title="Esa vista no existe"
              message="Revisá el ?view= de la URL."
              onRetry={() => setQuery({ view: "time" })}
            />
          </div>
        )}
      </Suspense>
    </AppShell>
  )
}
